/**
 * Camera preview: the mirrored video with the tracked skeleton drawn over it.
 *
 * It composites into a canvas rather than stacking a transparent overlay on the
 * video, for one concrete reason: `canvas.captureStream()` then yields a video
 * track that already contains the performance AND the tracking, so a recorded
 * take shows what the player actually did. Compositing at record time only
 * would mean two different pipelines to keep in agreement.
 *
 * The image is mirrored so the preview reads like a mirror. Landmarks are
 * mirrored with it; the mapping layer is fed the UNMIRRORED normalized
 * coordinates, which is why role assignment lives in vision/landmarks.ts and
 * not here.
 */

import type { RawHand } from '../vision/landmarks';
import { el } from './controls';

/** MediaPipe's 21-point hand skeleton. */
const CONNECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20],
  [0, 17],
];

/**
 * What the height axis points at. Structurally identical to the scenes'
 * Selection on purpose — declared here too so ui/ does not import from
 * scenes/ and the preview stays reusable.
 */
export interface LadderSelection {
  count: number;
  /** 0 at the BOTTOM: low hand, low zone. */
  active: number;
  label: string;
}

export interface Preview {
  readonly root: HTMLElement;
  readonly canvas: HTMLCanvasElement;
  /** Frames actually composited — 0 means the camera is not delivering. */
  readonly framesDrawn: number;
  attach(video: HTMLVideoElement): void;
  /** Hands to draw on the next frame, in normalized coordinates. */
  setHands(hands: readonly RawHand[]): void;
  /**
   * The zone ladder: what a trigger would play right now. Drawn into the
   * composite, so it shows in the live preview AND in recorded takes. Pass
   * null for scenes with no height-selected target.
   */
  setSelection(sel: LadderSelection | null): void;
  setNote(msg: string): void;
  start(): void;
  stop(): void;
  /** Video tracks for a take: the composited preview, not the raw camera. */
  captureTracks(fps?: number): MediaStreamTrack[];
  dispose(): void;
}

export function createPreview(): Preview {
  const canvas = el('canvas', { class: 'preview-canvas' }) as HTMLCanvasElement;
  const note = el('p', { class: 'note dim preview-note' }, ['camera off']);
  // The video sits BEHIND the canvas rather than nowhere.
  //
  // iOS Safari will not reliably decode a <video> that is not in the document:
  // a detached element yields no frames, so both drawImage and the landmarker
  // see nothing and the preview is simply black. Desktop Chromium is happy with
  // a detached element, which is exactly why this passes a headless test and
  // fails on a phone. It must be in the DOM and it must be rendered — display
  // none and visibility hidden both put us back where we started.
  const stack = el('div', { class: 'preview-stack' }, [canvas]);
  const root = el('div', { class: 'preview' }, [stack, note]);

  const ctx = canvas.getContext('2d');
  let video: HTMLVideoElement | null = null;
  let hands: readonly RawHand[] = [];
  let selection: LadderSelection | null = null;
  let raf = 0;
  let captured: MediaStream | null = null;
  let framesDrawn = 0;
  let startedAt = 0;

  function draw(): void {
    raf = requestAnimationFrame(draw);
    if (!ctx || !video) return;
    if (video.readyState < 2) {
      // Say so rather than showing an unexplained black rectangle.
      if (startedAt && performance.now() - startedAt > 4000 && !framesDrawn) {
        note.textContent = 'No camera frames yet — the stream is connected but not decoding.';
      }
      return;
    }
    framesDrawn++;

    // iOS routinely ignores the { ideal: 640 } constraint and hands back 720p or
    // more. Cap the WIDTH and derive the height from the source aspect ratio —
    // clamping both axes independently would squash a 16:9 stream into 4:3, in
    // the live preview and in every recorded take. A smaller composite also
    // keeps the canvas capture and H.264 encode away from the memory ceiling on
    // an older phone.
    const sw = video.videoWidth || 640;
    const sh = video.videoHeight || 480;
    const MAX_W = 640;
    const w = Math.min(sw, MAX_W);
    const h = Math.round((w / sw) * sh);
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }

    ctx.save();
    // Mirror so the preview behaves like a mirror rather than a photograph.
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);

    for (const hand of hands) {
      const p = hand.points;
      if (p.length < 21) continue;
      ctx.strokeStyle = 'rgba(120, 230, 200, 0.9)';
      ctx.lineWidth = Math.max(2, w / 260);
      ctx.beginPath();
      for (const [a, b] of CONNECTIONS) {
        ctx.moveTo(p[a][0] * w, p[a][1] * h);
        ctx.lineTo(p[b][0] * w, p[b][1] * h);
      }
      ctx.stroke();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      const r = Math.max(2.5, w / 200);
      for (const pt of p) {
        ctx.beginPath();
        ctx.arc(pt[0] * w, pt[1] * h, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();

    // The selection ladder — drawn AFTER restore, in unmirrored space, so the
    // label reads normally. Zone 0 at the bottom, matching `height` (a low
    // hand selects a low zone). This is the answer to "hard to know what I'd
    // get": the target is visible where the eyes already are, before the
    // pinch, and it rides into every recorded take via captureStream.
    if (selection && selection.count > 1) {
      const { count, active, label } = selection;
      const lw = Math.max(22, Math.min(40, Math.round(w * 0.055)));
      const x0 = w - lw;
      const zh = h / count;
      for (let i = 0; i < count; i++) {
        const y = h - (i + 1) * zh;
        const isActive = i === active;
        ctx.fillStyle = isActive ? 'rgba(95, 179, 161, 0.55)' : 'rgba(20, 24, 28, 0.45)';
        ctx.fillRect(x0, y, lw, zh - 1);
        if (isActive) {
          ctx.strokeStyle = 'rgba(95, 179, 161, 0.95)';
          ctx.lineWidth = 2;
          ctx.strokeRect(x0 + 1, y + 1, lw - 2, zh - 3);
        }
      }
      // Label beside the active zone, clamped so it never leaves the frame.
      const fs = Math.max(12, Math.round(w / 34));
      ctx.font = `600 ${fs}px ui-monospace, Menlo, monospace`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      const ly = Math.max(fs, Math.min(h - fs, h - (active + 0.5) * zh));
      const text = label.toUpperCase();
      const pad = 6;
      const tw = ctx.measureText(text).width;
      ctx.fillStyle = 'rgba(20, 24, 28, 0.75)';
      ctx.fillRect(x0 - tw - pad * 2 - 2, ly - fs * 0.75, tw + pad * 2, fs * 1.5);
      ctx.fillStyle = 'rgba(233, 227, 214, 0.98)';
      ctx.fillText(text, x0 - pad - 2, ly);
    }
  }

  return {
    root,
    canvas,
    get framesDrawn() { return framesDrawn; },
    attach(v) {
      video = v;
      v.classList.remove('camera-video-parked');
      v.classList.add('preview-video');
      // In the DOM, behind the canvas. See the note where `stack` is built.
      if (v.parentElement !== stack) {
        stack.insertBefore(v, canvas);
        // Safari can pause a video that is re-parented mid-playback. It is
        // muted and inline, so resuming needs no user gesture.
        void v.play().catch(() => { note.textContent = 'Camera stream stalled — reload and allow the camera.'; });
      }
    },
    setHands(h) { hands = h; },
    setSelection(sel) { selection = sel; },
    setNote(msg) { note.textContent = msg; },
    start() {
      startedAt = performance.now();
      if (!raf) raf = requestAnimationFrame(draw);
    },
    stop() { if (raf) { cancelAnimationFrame(raf); raf = 0; } },
    captureTracks(fps = 30) {
      if (typeof canvas.captureStream !== 'function') return [];
      if (!captured) captured = canvas.captureStream(fps);
      return captured.getVideoTracks();
    },
    dispose() {
      this.stop();
      captured?.getTracks().forEach((t) => t.stop());
      captured = null;
      video = null;
    },
  };
}
